import { TreeNodeDTO } from 'business/models/Models';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { allowedTargetsMap, AllowProjectParentNodes } from './constants';

export const buildTree = (nodes, parentId = null, depth = 1) => {
  const children = nodes.filter((node) => node.parentId === parentId);

  const tree = children.map((child) => ({
    ...child,
    depth: depth,
    submenuItems: buildTree(nodes, child.id, depth + 1),
  }));

  return tree.length ? tree : null;
};

const isDescendant = (node, targetNodeId) => {
  if (!node.children || !Array.isArray(node.children)) {
    return false;
  }
  for (const child of node.children) {
    if (child.id === targetNodeId) {
      return true;
    }
    if (isDescendant(child, targetNodeId)) {
      return true;
    }
  }
  return false;
};

export const copyPasteValidation = (nodesToCopy, targetNode) => {
  const unableToPasteNodes: { node: TreeNodeDTO; reason: string }[] = [];
  const copyNodes = [];

  if (!nodesToCopy) {
    return { unableToPasteNodes, copyNodes };
  }

  for (const node of nodesToCopy) {
    if (isDescendant(node, targetNode.id)) {
      unableToPasteNodes.push({
        node,
        reason: 'Parent node cannot be pasted in child node',
      });
      continue;
    }
  }

  const isPasteAllowedForRootProjectsTreeNode =
    targetNode?.type === ETreeNodeTypes.Projects &&
    nodesToCopy.length > 0 &&
    nodesToCopy.every((_node) =>
      [ETreeNodeTypes.Project, ETreeNodeTypes.ProjectsFolder].includes(
        _node.type as ETreeNodeTypes,
      ),
    );

  for (const node of nodesToCopy) {
    if (unableToPasteNodes.find((n) => n.node.id === node.id)) {
      continue;
    }
    if (node.id === targetNode?.id) {
      unableToPasteNodes.push({
        node,
        reason: `Cannot be pasted here`,
      });
      continue;
    }

    const targetType = targetNode?.type as ETreeNodeTypes;

    if (allowedTargetsMap[node.type]?.includes(targetType)) {
      copyNodes.push(node);
    } else if (isPasteAllowedForRootProjectsTreeNode) {
      copyNodes.push(node);
    } else {
      unableToPasteNodes.push({
        node,
        reason: `${
          node.type.includes('Folder')
            ? node.type.formatPascalCaseToCamelCaseString()
            : node.type
        } cannot be pasted in ${
          targetNode.type.includes('Folder')
            ? targetNode.type.formatPascalCaseToLowerCaseString()
            : targetNode.type
        }`,
      });
    }
  }
  return { unableToPasteNodes, copyNodes };
};

export const canAddProject = (node) => {
  return (
    AllowProjectParentNodes.includes(node?.type) ||
    node?.accessRightName !== ETreeNodeAccessRightNames.Modify
  );
};

type QuickAccessChildNode = {
  extra?: {
    quickAccessMeta?: {
      originalId?: number;
    };
  };
};

export const filterQuickAccessChildren = (
  children: QuickAccessChildNode[] | null | undefined,
  deletedNodeIds: number[],
): QuickAccessChildNode[] | undefined => {
  if (!Array.isArray(children)) {
    return undefined;
  }
  return children.filter(
    (node) => !deletedNodeIds.includes(node?.extra?.quickAccessMeta?.originalId),
  );
};
